/**
 * AIModelSettings (VH-258, extended by VH-280)
 *
 * The model every AI feature runs — the chat, the weekly plan writer, the report
 * narratives, the translations — in one place. Before this, nine photo analyzers were
 * configurable (VH-117) and everything else had its model typed into the code: the
 * cycle chat ran `claude-opus-5`, which the admin could not even display.
 *
 * The dropdown is what the provider says it serves, fetched server-side and cached for
 * a day, so a model that shipped this morning is selectable this morning. When a
 * provider cannot be reached the list falls back to known names and says so, because an
 * empty dropdown reads as a broken feature.
 *
 * VH-280 (OpenAI retires gpt-4o / 4o-mini / 4.1 / 4.1-mini): every OpenAI feature also
 * has a fallback and a reasoning effort; the retiring models carry a tag in every list;
 * a feature that fell back is marked red; each feature shows its calls this week against
 * last; and the photo analyzers (set on LLM Settings) are listed read-only, so every
 * OpenAI call VitaHub makes is on this one screen.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { adminAPI } from '../../services/api'
import AIModelFeatureRow from './AIModelFeatureRow'
import HubManagedNotice from './HubManagedNotice'

const initialChoices = (features) =>
  Object.fromEntries(features.map((f) => [f.key, {
    model: f.model,
    fallback: f.fallback_model ?? '',
    effort: f.effort ?? f.effort_default ?? '',
  }]))

/** The update for one feature: the model always, fallback and effort only when changed. */
function buildUpdate(f, choice) {
  const update = { key: f.key, model: choice.model }
  if (f.provider === 'openai') {
    if (choice.fallback !== (f.fallback_model ?? '')) update.fallback_model = choice.fallback
    if (choice.effort !== (f.effort ?? f.effort_default ?? '')) update.effort = choice.effort
  }
  return update
}

const isChanged = (f, choice) => {
  if (!choice) return false
  const u = buildUpdate(f, choice)
  return u.model !== f.model || 'fallback_model' in u || 'effort' in u
}

export default function AIModelSettings() {
  const { t } = useTranslation()
  const [data, setData] = useState({
    features: [], analyzers: [], retiring_models: [], efforts: [], hub_managed: false, hub_site: null,
  })
  const [usage, setUsage] = useState({})
  const [choices, setChoices] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)

  const apply = useCallback((payload) => {
    const next = {
      features: payload?.features ?? [],
      analyzers: payload?.analyzers ?? [],
      retiring_models: payload?.retiring_models ?? [],
      efforts: payload?.efforts ?? [],
      // HP-34: HubPlatform holds the settings; this screen only shows them.
      hub_managed: !!payload?.hub_managed,
      hub_site: payload?.hub_site ?? null,
    }
    setData(next)
    setChoices(initialChoices(next.features))
  }, [])

  const loadUsage = useCallback(async () => {
    // Usage is a help, not the screen: if the log cannot be read the settings still work.
    try {
      const { data: u } = await adminAPI.getAIUsage()
      setUsage(Object.fromEntries((u?.features ?? []).map((f) => [f.key, f])))
    } catch {
      setUsage({})
    }
  }, [])

  const load = useCallback(async (refresh = false) => {
    try {
      if (refresh) setRefreshing(true)
      else setLoading(true)
      setError(null)
      const { data: payload } = await adminAPI.getAIModelSettings(refresh)
      apply(payload)
    } catch {
      setError('admin.aiModels.errors.load')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [apply])

  useEffect(() => { load(); loadUsage() }, [load, loadUsage])

  const retiring = useMemo(() => new Set(data.retiring_models), [data.retiring_models])
  const changed = data.features.filter((f) => isChanged(f, choices[f.key]))

  const onChange = (key, patch) => setChoices((c) => {
    const next = { ...c[key], ...patch }
    // A fallback equal to the model is no fallback; the server refuses it, so reset it.
    if (patch.model && patch.model === next.fallback) next.fallback = ''
    return { ...c, [key]: next }
  })

  const send = async (updates, successKey) => {
    try {
      setSaving(true)
      const { data: payload } = await adminAPI.updateAIModelSettings(updates)
      apply(payload)
      toast.success(t(successKey, { count: updates.length }))
    } catch (err) {
      // The server refuses a model the provider does not serve; say which, not "failed".
      toast.error(err?.response?.data?.detail || t('admin.aiModels.errors.save'))
    } finally {
      setSaving(false)
    }
  }

  const save = () => send(changed.map((f) => buildUpdate(f, choices[f.key])), 'admin.aiModels.saved')
  // Saving a feature unchanged is "the setting was changed" for the red mark.
  const clearMark = (f) => send([{ key: f.key, model: f.model }], 'admin.aiModels.markCleared')

  if (loading) return <p className="text-sm text-gray-500">{t('common.loading')}</p>
  if (error) return <p className="text-sm text-red-600" role="alert">{t(error)}</p>

  const stale = data.features.some((f) => !f.models_live)
  const marked = [...data.features, ...data.analyzers].filter((f) => f.missing_model).length
  const readOnly = data.hub_managed

  return (
    <section className="bg-surface-container rounded-lg border border-gray-200 p-5" data-testid="ai-model-settings">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('admin.aiModels.title')}</h2>
          <p className="text-sm text-gray-600 mt-1">{t('admin.aiModels.intro')}</p>
          <p className="text-xs text-gray-500 mt-1">{t('admin.aiModels.fallbackIntro')}</p>
        </div>
        <button type="button" className="btn-outline text-sm flex items-center gap-1"
          disabled={refreshing} onClick={() => { load(true); loadUsage() }} data-testid="refresh-models">
          <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
          {t('admin.aiModels.refresh')}
        </button>
      </div>

      {readOnly && <HubManagedNotice site={data.hub_site} />}

      {marked > 0 && (
        <p className="mt-3 text-sm text-red-700 font-medium" role="alert" data-testid="ai-missing-summary">
          {t('admin.aiModels.missingSummary', { count: marked })}
        </p>
      )}

      {stale && (
        <p className="mt-3 text-xs text-amber-800 flex items-start gap-1" data-testid="models-stale">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>{t('admin.aiModels.stale')}</span>
        </p>
      )}

      <ul className="mt-4 divide-y divide-gray-100">
        {data.features.map((f) => (
          <AIModelFeatureRow key={f.key} feature={f} choice={choices[f.key]} onChange={onChange}
            retiring={retiring} efforts={data.efforts} usage={usage[f.key]}
            onClearMark={clearMark} busy={saving} readOnly={readOnly} hubManaged={readOnly} />
        ))}
      </ul>

      {!readOnly && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button type="button" className="btn-primary text-sm" data-testid="save-ai-models"
            disabled={saving || changed.length === 0} onClick={save}>
            {saving ? t('common.saving') : t('admin.aiModels.save', { count: changed.length })}
          </button>
          {changed.length > 0 && (
            <span className="text-xs text-gray-600">{t('admin.aiModels.takesEffect')}</span>
          )}
        </div>
      )}

      {data.analyzers.length > 0 && (
        <div className="mt-8" data-testid="ai-analyzers">
          <h3 className="text-base font-semibold text-gray-900">{t(readOnly ? 'admin.aiModels.analyzers.titleHub' : 'admin.aiModels.analyzers.title')}</h3>
          <p className="text-xs text-gray-500 mt-1">{t(readOnly ? 'admin.aiModels.analyzers.introHub' : 'admin.aiModels.analyzers.intro')}</p>
          <ul className="mt-2 divide-y divide-gray-100">
            {data.analyzers.map((f) => (
              <AIModelFeatureRow key={f.key} feature={f} retiring={retiring}
                usage={usage[f.key]} readOnly hubManaged={readOnly} />
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
